import { Injectable } from '@angular/core';
import {
    AppTrackingEvents,
    InitialTrackingEvent,
    TrackingEvent,
    TrackingEventPayLoad,
    TrackingInput,
} from '@app/@core/models/tracking-event.model';
import { MetadataService } from '../metadata.service';
import { HttpClient } from '@angular/common/http';
import { ReferrerService } from '../referrer.service';
import { catchError, first, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { environment } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class InstrumentationService {
    private initialTrackingEvent: InitialTrackingEvent | null = null;
    private readonly trackingAPIUrl = environment.trackingApiUrl;
    private readonly isProd = environment.production;

    constructor(
        private readonly metadataService: MetadataService,
        private readonly http: HttpClient,
        private readonly referrerService: ReferrerService
    ) {}

    /**
     * Public API — log any event with merged metadata
     */
    logEvent<T extends AppTrackingEvents>(
        trackingInput: TrackingInput<T>
    ): void {
        this.ensureInitialTrackingEvent();

        const { eid, ...eventFields } = trackingInput;

        const trackingEvent = this.buildTrackingEvent(eventFields as T);

        this.sendToServer(
            this.filterUndefined(trackingEvent) as TrackingEvent<T>,
            eid || this.metadataService.getEid()
        );
    }

    /**
     * Initialize once per session
     */
    private ensureInitialTrackingEvent(): void {
        if (this.initialTrackingEvent) {
            return;
        }

        const { os, browser, version } = this.metadataService.getOSInfo();

        this.initialTrackingEvent = {
            eventName: 'UserLoggedEvent',
            timestamp: this.now(),
            solvId: this.metadataService.getSolvId() || '',
            sessionId: this.metadataService.getSessionId() || '',
            pageUrl: window.location.href,
            referrerUrl: this.referrerService.getReferrer(),
            eventSource: '',
            eventLabel: '',
            deviceType: this.metadataService.getDeviceType(),
            operatingSystem: os,
            browser,
            browserVersion: version,
        };

        this.debugLog(
            'Initialized initial tracking event',
            this.initialTrackingEvent
        );
    }

    /**
     * Build final merged tracking event
     */
    private buildTrackingEvent<T>(eventFields: T): TrackingEvent<T> {
        return {
            ...this.initialTrackingEvent!,
            ...eventFields,
            solvId: this.metadataService.getSolvId(),
            sessionId: this.metadataService.getSessionId(),
            referrerUrl: this.referrerService.getReferrer(),
            pageUrl: window.location.href,
            timestamp: this.now(),
        };
    }

    /**
     * Update stored initialTrackingEvent fields
     */
    updateInitialTrackingEvent(updates: Partial<InitialTrackingEvent>): void {
        if (!this.initialTrackingEvent) {
            return;
        }
        this.initialTrackingEvent = {
            ...this.initialTrackingEvent,
            ...updates,
        };
    }

    /**
     * Expose initial event state
     */
    getInitialTrackingEvent(): InitialTrackingEvent | null {
        return this.initialTrackingEvent;
    }

    /**
     * Send payload to backend
     */
    private sendToServer<T>(
        trackingEvent: TrackingEvent<T>,
        eid: string
    ): void {
        const payload = [
            new TrackingEventPayLoad({ eid, properties: trackingEvent }),
        ];

        this.http
            .post(this.trackingAPIUrl, payload)
            .pipe(
                first(),
                tap((res) => this.debugLog('Event sent successfully', res)),
                catchError((err) => {
                    console.error(
                        '[Instrumentation] Failed to send event:',
                        err
                    );
                    return of(null);
                })
            )
            .subscribe({
                error: (err) =>
                    console.error('[Instrumentation] Subscription error:', err),
            });
    }

    /**
     * Remove undefined, null, or empty string values
     */
    private filterUndefined<T extends Record<string, any>>(obj: T): Partial<T> {
        return Object.fromEntries(
            Object.entries(obj).filter(
                ([, value]) =>
                    value !== undefined && value !== null && value !== ''
            )
        ) as Partial<T>;
    }

    /** Consistent timestamp generator */
    private now(): number {
        return Date.now();
    }

    /** Debug logger (auto-disabled in production) */
    private debugLog(message: string, data?: unknown): void {
        if (!this.isProd) {
            console.log(`[Instrumentation] ${message}:`, data ?? '');
        }
    }
}
